import { TypedDocumentNode, gql } from '@apollo/client';

import {
  Score,
  Props as ScoreProps,
} from '@sorare/core/src/components/scoring/Score';
import { isType } from '@sorare/core/src/lib/gql';

import type { So5AppearanceScore_so5Appearance } from './__generated__/index.graphql';

type Props = {
  so5Appearance: So5AppearanceScore_so5Appearance;
  shape?: 'TEXT' | 'PILL';
  withBonus?: boolean;
} & Pick<ScoreProps, 'withDate' | 'hideReviewing' | 'forceScoreStatus'>;

export const So5AppearanceScore = ({
  so5Appearance,
  shape = 'PILL',
  withBonus = false,
  ...rest
}: Props) => {
  const {
    score,
    scoreWithoutBonus,
    scoreStatus,
    upcomingGame,
    playedGames,
    playerGameScore,
  } = so5Appearance;

  const showPlayerScore = !withBonus;

  return (
    <Score
      shape={shape}
      score={showPlayerScore ? scoreWithoutBonus : score}
      scoreStatus={scoreStatus}
      upcomingGame={upcomingGame || undefined}
      hasPlayedGames={playedGames.length > 0}
      scoreType={showPlayerScore ? 'PLAYER_SCORE' : 'CARD_SCORE'}
      fieldStatus={
        playerGameScore &&
        isType(playerGameScore.anyPlayerGameStats, 'PlayerGameStats')
          ? playerGameScore.anyPlayerGameStats.fieldStatus
          : undefined
      }
      {...rest}
    />
  );
};

So5AppearanceScore.fragments = {
  so5Appearance: gql`
    fragment So5AppearanceScore_so5Appearance on So5AppearanceInterface {
      id
      score
      scoreWithoutBonus: score(withBonus: false)
      scoreStatus
      upcomingGame {
        id
        ...Score_anyGameInterface
      }
      playedGames {
        id
      }
      playerGameScore {
        id
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            fieldStatus
          }
        }
      }
    }
    ${Score.fragments.anyGameInterface}
  ` as TypedDocumentNode<So5AppearanceScore_so5Appearance>,
};
